import EmberService, { service } from '@ember/service';

import type { Service } from 'webapp-types';
import { AppEvent } from 'webapp/enums/events';

import type DChart from 'webapp/d-chart/d-chart';

// Staggers Highcharts chart creation — one chart per animation frame — to avoid layout thrashing.
// When multiple charts initialize in the same frame, each chart's DOM writes invalidate layout,
// and the next chart's getComputedStyle calls force expensive synchronous recalculations.
// Spreading chart creation across frames reduces peak frame load by 3-5x.

export default class ChartRenderQueueService extends EmberService {
	@service('event-manager') declare eventManagerService: Service.EventManager;

	private queue: DChart[] = [];
	private isProcessing = false;

	add(dChart: DChart): void {
		this.queue.push(dChart);
		this.scheduleProcess();
	}

	remove(dChart: DChart): void {
		const idx = this.queue.indexOf(dChart);
		if (idx !== -1) {
			this.queue.splice(idx, 1);
		}
	}

	checkIfHasWork() {
		return this.isProcessing || !!this.queue.length;
	}

	private scheduleProcess(): void {
		if (this.isProcessing) {
			return;
		}
		this.isProcessing = true;
		requestAnimationFrame(() => this.processNext());
	}

	private processNext(): void {
		const dChart = this.queue.shift();

		if (!dChart) {
			this.isProcessing = false;
			this.eventManagerService.trigger(AppEvent.CHART_RENDER_QUEUE_DONE);
			return;
		}

		dChart.syncCreateChart();
		requestAnimationFrame(() => this.processNext());
	}
}

declare module '@ember/service' {
	interface Registry {
		'chart-render-queue': ChartRenderQueueService;
	}
}
